/* SSL Face Mesh — MediaPipe FaceLandmarker wrapper.
 *
 * Loads on demand from the jsdelivr CDN (~2MB WASM + model). Single shared
 * landmarker instance reused across calls. Detects 478 facial landmarks
 * on the supplied <img> and renders three layered overlays on a canvas:
 *
 *   1. Lab-schematic CALIPERS — eye-spacing measurement, vertical
 *      symmetry line, brow + lip horizontal references. Warm-gold strokes
 *      with subtle glow. Visible at full opacity.
 *
 *   2. Aurora HEATMAP — radial gradient blobs at named face zones tinted
 *      by signal scores. Brighter where the worker flagged a concern.
 *      Drawn with `screen` blend for that ambient-glow feel.
 *
 *   3. Wireframe MESH — faint white tesselation across the whole face.
 *      Sits behind everything at low alpha.
 *
 * The drawing function `render(canvas, detection, signals)` paints all
 * three to one canvas in a single pass. CSS staggered opacity transitions
 * on three stacked canvases drive the reveal sequence.
 *
 * Public API (window.SSLFaceMesh):
 *   detect(img)            → { landmarks, naturalWidth, naturalHeight } | null
 *   calipers(landmarks)    → { eyeSpacing, featureRatio, points }
 *   renderMesh(canvas, detection)
 *   renderHeatmap(canvas, detection, signals)
 *   renderCalipers(canvas, detection)
 */
(function () {
  const MEDIAPIPE_VERSION = '0.10.20';
  const VISION_BUNDLE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_VERSION}/vision_bundle.mjs`;
  const WASM_URL       = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_VERSION}/wasm`;
  const MODEL_URL      = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

  let landmarker = null;
  let loading = null;

  async function getLandmarker() {
    if (landmarker) return landmarker;
    if (loading)    return loading;
    loading = (async () => {
      const mod = await import(VISION_BUNDLE);
      const { FaceLandmarker, FilesetResolver } = mod;
      const fileset = await FilesetResolver.forVisionTasks(WASM_URL);
      landmarker = await FaceLandmarker.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: MODEL_URL,
          /* GPU on browsers that support WebGPU/WebGL2; CPU fallback is
           * automatic. Either runs detect() in well under 100ms after init. */
          delegate: 'GPU',
        },
        runningMode: 'IMAGE',
        numFaces: 1,
        outputFaceBlendshapes: false,
        outputFacialTransformationMatrixes: false,
      });
      return landmarker;
    })();
    return loading;
  }

  /* Wait for img to fully load before detection. Data-URL or CDN images
   * may already be complete when this is called; bail fast in that case. */
  function ensureLoaded(img) {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return new Promise((resolve, reject) => {
      img.addEventListener('load',  resolve, { once: true });
      img.addEventListener('error', reject,  { once: true });
    });
  }

  async function detect(img) {
    try {
      await ensureLoaded(img);
      const lm = await getLandmarker();
      const result = lm.detect(img);
      if (!result.faceLandmarks || !result.faceLandmarks.length) return null;
      return {
        landmarks: result.faceLandmarks[0],          // 478 normalized points {x,y,z}
        naturalWidth:  img.naturalWidth,
        naturalHeight: img.naturalHeight,
      };
    } catch (e) {
      console.warn('[ssl-face-mesh] detection failed', e);
      return null;
    }
  }

  /* MediaPipe FaceMesh landmark indices.
   * (Reference: https://github.com/google-ai-edge/mediapipe/blob/master/mediapipe/python/solutions/face_mesh_connections.py) */
  const I = {
    forehead:        10,
    chin:            152,
    leftCheek:       234,
    rightCheek:      454,
    leftEyeInner:    133,
    rightEyeInner:   362,
    leftEyeOuter:    33,
    rightEyeOuter:   263,
    leftBrow:        70,
    rightBrow:       300,
    noseTip:         1,
    upperLip:        13,
    lowerLip:        14,
  };

  /* Compute measurements from normalized landmarks. Skincare-relevant:
   *   symmetry — how balanced the face is left/right (0–1, higher = more
   *              symmetrical). Computed from outer-eye-to-nose distance.
   *   featureRatio — face height ÷ width, retained internally but not
   *                  surfaced in the UI (no skincare bearing).
   */
  function calipers(lm) {
    const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const faceW = dist(lm[I.leftCheek],   lm[I.rightCheek]);
    const faceH = dist(lm[I.forehead],    lm[I.chin]);
    const eyeSpan = dist(lm[I.leftEyeInner], lm[I.rightEyeInner]);
    /* Symmetry: ratio of shorter side to longer side, eye-outer to nose-tip. */
    const dl = dist(lm[I.leftEyeOuter],  lm[I.noseTip]);
    const dr = dist(lm[I.rightEyeOuter], lm[I.noseTip]);
    const symmetry = (Math.min(dl, dr) / Math.max(dl, dr));
    return {
      symmetry:     symmetry.toFixed(2),                   // 0.00 → 1.00
      eyeSpacing:   (eyeSpan / faceW).toFixed(2),          // unused in UI now
      featureRatio: (faceH   / faceW).toFixed(3),          // unused in UI now
      faceWidth:    faceW,
      points: {
        forehead:      lm[I.forehead],
        chin:          lm[I.chin],
        leftCheek:     lm[I.leftCheek],
        rightCheek:    lm[I.rightCheek],
        leftEyeInner:  lm[I.leftEyeInner],
        rightEyeInner: lm[I.rightEyeInner],
        leftBrow:      lm[I.leftBrow],
        rightBrow:     lm[I.rightBrow],
        noseTip:       lm[I.noseTip],
        upperLip:      lm[I.upperLip],
        lowerLip:      lm[I.lowerLip],
      }
    };
  }

  /* Sets canvas backing-store size to match its CSS display size scaled
   * by devicePixelRatio. Returns the display dimensions for ctx
   * arithmetic. */
  function sizeCanvas(canvas) {
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = Math.round(rect.width  * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { ctx, w: rect.width, h: rect.height };
  }

  /* Project a normalized landmark to display pixels.
   * The img is rendered with object-fit: cover, so we apply the same
   * crop math here — landmarks live in image-natural coordinates and
   * need to be mapped onto the cropped display rect. */
  function project(lm, w, h, natW, natH) {
    const imgRatio = natW / natH;
    const boxRatio = w / h;
    let scale, dx, dy;
    if (imgRatio > boxRatio) {
      /* Image is wider than box: cover crops left/right. */
      scale = h / natH;
      dx = (w - natW * scale) / 2;
      dy = 0;
    } else {
      /* Image is taller than box: cover crops top/bottom. */
      scale = w / natW;
      dx = 0;
      dy = (h - natH * scale) / 2;
    }
    return (point) => ({
      x: dx + point.x * natW * scale,
      y: dy + point.y * natH * scale,
    });
  }

  /* ── LAYER 1 · MESH WIREFRAME ────────────────────────────────────── */
  /* MediaPipe FaceLandmarker doesn't expose tesselation indices in the JS
   * bundle (Python only). We render an approximation by connecting each
   * landmark to its nearest 4 neighbours — fast, looks like a mesh, no
   * extra deps. */
  function renderMesh(canvas, det) {
    const { ctx, w, h } = sizeCanvas(canvas);
    const proj = project(null, w, h, det.naturalWidth, det.naturalHeight);
    const pts = det.landmarks.map(proj);
    ctx.lineWidth = 0.4;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
    /* For each point, find 3 nearest neighbours and draw a line.
     * O(n²) but n=478 — runs in <10ms. */
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const dists = [];
      for (let j = 0; j < pts.length; j++) {
        if (j === i) continue;
        const b = pts[j];
        const d = (a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y);
        dists.push({ j, d });
      }
      dists.sort((x, y) => x.d - y.d);
      for (let k = 0; k < 3; k++) {
        const b = pts[dists[k].j];
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    }
  }

  /* ── LAYER 2 · AURORA HEATMAP ───────────────────────────────────── */
  /* Each face zone gets a radial gradient blob whose radius and alpha
   * scale with the relevant signal score. Drawn with `screen` blend so
   * blobs add light over the photo without darkening it. */
  const ZONE_LANDMARKS = {
    forehead:      [10, 67, 109, 9, 151, 108, 69, 104, 105, 66, 107],
    tzone:         [9, 8, 168, 6, 197, 195, 5, 4],
    cheekL:        [50, 36, 116, 117, 118, 119, 100, 47, 187],
    cheekR:        [280, 266, 345, 346, 347, 348, 329, 277, 411],
    perioralUpper: [0, 13, 37, 39, 40, 267, 269, 270, 14, 17],
    perioral:      [17, 84, 181, 91, 314, 405, 321, 375, 18, 200],
    periorbitalL:  [33, 7, 163, 144, 145, 153, 154, 155, 246, 161, 160, 159, 158, 157],
    periorbitalR:  [263, 249, 390, 373, 374, 380, 381, 382, 466, 388, 387, 386, 385, 384],
    nose:          [1, 2, 5, 4, 6, 168, 195, 197, 196, 419],
    jawline:       [172, 136, 150, 149, 176, 148, 152, 377, 400, 378, 379, 365, 397],
  };

  function centroid(landmarks, indices) {
    let x = 0, y = 0;
    indices.forEach(i => { x += landmarks[i].x; y += landmarks[i].y; });
    return { x: x / indices.length, y: y / indices.length };
  }

  /* Map worker signal scores (0–100) into per-zone intensity (0–1). */
  function zoneIntensities(signals, ext) {
    const s  = signals || {};
    const e  = ext || {};
    const n = (v) => Math.max(0, Math.min(100, v || 0)) / 100;
    return {
      forehead:      Math.max(n(e.fine_lines_forehead),  n(e.blemish_forehead)),
      tzone:         n(e.oiliness_tzone || s.oiliness_score),
      cheekL:        Math.max(n(e.oiliness_uzone), n(e.dryness_cheeks), n(e.pore_density_cheeks)),
      cheekR:        Math.max(n(e.oiliness_uzone), n(e.dryness_cheeks), n(e.pore_density_cheeks)),
      perioralUpper: n(e.dryness_perioral),
      perioral:      n(e.dryness_perioral),
      periorbitalL:  Math.max(n(e.undereye_darkness), n(e.undereye_puffiness)),
      periorbitalR:  Math.max(n(e.undereye_darkness), n(e.undereye_puffiness)),
      nose:          n(e.pore_density_nose),
      jawline:       n(e.blemish_jawline),
    };
  }

  function renderHeatmap(canvas, det, signals, ext) {
    const { ctx, w, h } = sizeCanvas(canvas);
    const proj = project(null, w, h, det.naturalWidth, det.naturalHeight);
    const intensities = zoneIntensities(signals, ext);
    /* Plain source-over compositing — the canvas-level mix-blend-mode:
     * screen handles photo blending. Inner screen composite was washing
     * out the gradient against transparent canvas pixels. */

    Object.keys(ZONE_LANDMARKS).forEach(zoneKey => {
      const intensity = intensities[zoneKey];
      /* Always paint at least a faint blob so something is visible even
       * on very-clean scans (otherwise the heatmap layer reads as broken). */
      const i = Math.max(0.18, intensity);
      const c  = centroid(det.landmarks, ZONE_LANDMARKS[zoneKey]);
      const cp = proj(c);
      const radius = (det.naturalWidth * 0.11) * (w / det.naturalWidth) * (1 + intensity * 0.7);
      const grad = ctx.createRadialGradient(cp.x, cp.y, 0, cp.x, cp.y, radius);
      /* Warm gold tint, intensity-scaled. Higher base alpha so signals
       * still register through `mix-blend-mode: screen` against tan photos. */
      const alpha = Math.min(0.85, 0.40 + i * 0.55);
      grad.addColorStop(0,    `rgba(255, 215, 145, ${alpha.toFixed(2)})`);
      grad.addColorStop(0.50, `rgba(220, 175, 110, ${(alpha * 0.55).toFixed(2)})`);
      grad.addColorStop(1,    'rgba(201, 181, 138, 0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cp.x, cp.y, radius, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  /* ── LAYER 3 · CALIPERS / LAB-SCHEMATIC LINES ──────────────────── */
  function renderCalipers(canvas, det) {
    const { ctx, w, h } = sizeCanvas(canvas);
    const proj = project(null, w, h, det.naturalWidth, det.naturalHeight);
    const lm = det.landmarks;

    const fore  = proj(lm[I.forehead]);
    const chin  = proj(lm[I.chin]);
    const lEye  = proj(lm[I.leftEyeInner]);
    const rEye  = proj(lm[I.rightEyeInner]);
    const lBrow = proj(lm[I.leftBrow]);
    const rBrow = proj(lm[I.rightBrow]);
    const upLip = proj(lm[I.upperLip]);
    const loLip = proj(lm[I.lowerLip]);
    const lCheek = proj(lm[I.leftCheek]);
    const rCheek = proj(lm[I.rightCheek]);

    ctx.strokeStyle = 'rgba(243, 226, 192, 0.85)';
    ctx.lineWidth = 0.45;
    ctx.shadowColor = 'rgba(201, 181, 138, 0.55)';
    ctx.shadowBlur = 4;

    /* Vertical center line — forehead → chin */
    line(ctx, fore.x, 0, fore.x, h);

    /* Brow horizontal */
    line(ctx, 0, lBrow.y, w, lBrow.y);
    /* Eye horizontal */
    line(ctx, 0, (lEye.y + rEye.y) / 2, w, (lEye.y + rEye.y) / 2);
    /* Lip horizontal */
    line(ctx, 0, (upLip.y + loLip.y) / 2, w, (upLip.y + loLip.y) / 2);

    /* Cheek width caliper — small ticks */
    tick(ctx, lCheek.x, h - 22, 8);
    tick(ctx, rCheek.x, h - 22, 8);
    line(ctx, lCheek.x, h - 22, rCheek.x, h - 22);

    /* Eye-spacing caliper — between inner corners */
    const eyeY = (lEye.y + rEye.y) / 2 - 14;
    tick(ctx, lEye.x, eyeY, 5);
    tick(ctx, rEye.x, eyeY, 5);
    line(ctx, lEye.x, eyeY, rEye.x, eyeY);

    /* Corner markers — top-left + top-right brackets */
    ctx.shadowBlur = 0;
    bracket(ctx, 6, 6, 14, 'tl');
    bracket(ctx, w - 6, 6, 14, 'tr');
    bracket(ctx, 6, h - 6, 14, 'bl');
    bracket(ctx, w - 6, h - 6, 14, 'br');
  }

  function line(ctx, x1, y1, x2, y2) {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  function tick(ctx, x, y, len) {
    line(ctx, x, y - len / 2, x, y + len / 2);
  }
  function bracket(ctx, x, y, len, corner) {
    const dx = corner.includes('r') ? -1 : 1;
    const dy = corner.includes('b') ? -1 : 1;
    line(ctx, x, y, x + dx * len, y);
    line(ctx, x, y, x, y + dy * len);
  }

  window.SSLFaceMesh = {
    detect,
    calipers,
    renderMesh,
    renderHeatmap,
    renderCalipers,
  };
})();
